/* Copyright 2019 The MathWorks, Inc. */
define(['mw-widget-api/defineWidget',
  'mw-widget-api/WidgetBase',
  'mw-progress-indicator/view/SpinnerView',
  'mw-progress-indicator/enums/TypeEnum',
  'mw-progress-indicator/enums/SizeEnum'
], function (defineWidget, WidgetBase, SpinnerView, TypeEnum, SizeEnum) {
  'use strict';

  const baseClass = 'mw-progress-indicator';
  /**
     * Progress Indicator widget can be created in 3 ways:
     * <pre>
     *  1. Using the ProgressIndicator class
     *  2. Using createElement
     *  3. Using HTML
     *
     * </pre>
     * 1. Using ProgressIndicator class:
     * Progress Indicator can be created by passing in an object to set its
     * properties.
     *
     * NOTE: DO NOT use new operator while creating the Progress Indicator object
     * <pre>
     * config = {
     *    type : 'spinner',
     *     value : 0
     * }
     * let progressIndicator= ProgressIndicator(config);
     * document.body.appendChild(progressIndicator);
     * </pre>
     *
     * 2. Using createElement
     * Progress Indicator can also be created using createElement in the code
     * <pre>
     * let progressIndicator = document.createElement('mw-progress-indicator');
     * progressIndicator.size='xsmall';
     * document.body.appendChild(progressIndicator);
     * </pre>
     *
     * 3. Using HTML
     * Progress Indicator  can be added as a tag in the html
     *<pre>
     * &lt;mw-progress-indicator size=”small” &gt;&lt;/mw-progress-indicator&gt;
     *</pre>
     *
     *
     * @param {Object} config An object which can be used to configure the properties.
     * <pre>
     * config = {
     *   size: 'small',
     *   type: 'Spinner'
     * }
     * let progressIndicator= ProgressIndicator(config);
     * document.body.appendChild(progressIndicator);
     * </pre>
     *
     */

  class ProgressIndicator extends WidgetBase {
    static get _observedAttributes () {
      return ['value', 'size', 'type'];
    }

    constructor () {
      super();
      this._artNode = document.createElement('div');
      this._progressNode = document.createElement('div');
    }

    init () {
      super.init();
      this._artNode.classList.add(`${baseClass}-Art`);
      this._root.appendChild(this._artNode);

      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'progress');
      }
      if (!this.hasAttribute('tabindex')) {
        this.setAttribute('tabindex', 0);
      }

      if (!this.hasAttribute('value')) {
        this.setAttribute('indeterminate', '');
      }
      // Set the default attributes
      // NOTE: The default type should be changed when the progress indicator is extended to progress bar
      if (!this.hasAttribute('type')) {
        this.setAttribute('type', 'spinner');
      }

      if (!this.hasAttribute('size')) {
        this.setAttribute('size', 'small');
      }
      this._updateSize(this.size);
      this._updateStroke();
    }

    /**
         * Set 'type' attribute on the Progress Indicator. This attribute take value:
         * <pre>
         * 1. Spinner
         * </pre>
         *
         * If the type is not defined, the default type will be Spinner.
         *
         * @type {String}
         * @default spinner
         * @example
         * config = {
         *    type : 'spinner',
         *     value : 0
         * }
         * let progressIndicator= ProgressIndicator(config);
         *
         */
    set type (value) {
      this._validate(value, 'type');
      value = value.toLowerCase();
      this.view = new SpinnerView();
      this.setAttribute('type', value);
    }
    get type () {
      return this.getAttribute('type');
    }

    /**
         * Set 'size' attribute on the Progress Indicator. This attribute can take 5 values:
         * <pre>
         * 1. xsmall
         * 2. small
         * 3. medium
         * 4. large
         * 5. xlarge
         * </pre>
         *
         * If the size is not defined, the default size will be small.
         *
         * @type {String}
         * @default small
         * @example
         *
         *
         * let progressIndicator= ProgressIndicator();
         * progressIndicator.size= medium;
         *
         *
         */
    set size (value) {
      this._validate(value, 'size');
      value = value.toLowerCase();
      this.setAttribute('size', value);
    }
    get size () {
      return this.getAttribute('size');
    }

    get indeterminate () {
      let state = this.hasAttribute('indeterminate');
      return state;
    }
    /**
         * 'value' attribute of the Progress Indicator determines whether the progress indicator
         * is determinate or indeterminate.
         * If the value attribute is set, then the progress indicator will be determinate and the
         * value attribute holds the current progress value.
         * The value attribute has to be updated to show the progress.
         *
         * If the value is not defined, the progress indicator will be indeterminate
         *
         *
         * @type {number}
         * @example
         *
         *
         * config = {
         *     value : 0
         * }
         * let progressIndicator= ProgressIndicator(config);
         * progressIndicator.value = 10;
         */
    set value (newValue) {
      this._validate(newValue, 'value');
      this.setAttribute('value', newValue);
      this.setAttribute('aria-valuenow', newValue);
      this.removeAttribute('indeterminate');
    }

    get value () {
      let val = this.getAttribute('value');
      if (val === null) {
        return 0;
      } else {
        return parseFloat(val);
      }
    }

    attributeChangedCallback (name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      // Prevent recursive sets from property vs attribute changes
      if (oldValue !== newValue) {
        switch (name) {
          case 'value':
            this.value = newValue;
            this._updateStroke();
            break;
          case 'type':
            this.type = newValue;
            break;
          case 'size':
            this.size = newValue;
            this._updateSize(this.size);
            break;
        }
      }
    }

    _updateStroke () {
      var strokeGap = this.valueLength;
      let strokeDash = this.value * this.valueLength / 100;
      if (this.indeterminate) {
        strokeDash = strokeGap / 4;
      }

      this._progressNode.setAttribute('stroke-dasharray', `${strokeDash}, ${strokeGap}`);
    }
    _updateSize (size) {
      if (!this.view) {
        return;
      }
      var { svg, valueLength } = this.view.createWidget(size);
      this.valueLength = valueLength;
      if (this._artNode.children.length === 1) {
        this._artNode.removeChild(this._artNode.firstElementChild);
        this._artNode.appendChild(svg.cloneNode(true));

        this._progressNode = this._artNode.getElementsByClassName(`${baseClass}`)[0];
        this._updateStroke();
      } else {
        this._artNode.appendChild(svg.cloneNode(true));
        this._progressNode = this._artNode.getElementsByClassName(`${baseClass}`)[0];
      }
    }
    _validate (value, property) {
      switch (property) {
        case 'size':
          let sizes = Object.values(SizeEnum);
          if ((typeof value !== 'string') || !sizes.includes(value.toLowerCase())) {
            throw new Error('Value of ' + property + ' property should be xsmall/small/medium/large/xlarge');
          }
          break;
        case 'type':
          if ((typeof value !== 'string') || !(value.toLowerCase() === TypeEnum.SPINNER)) {
            throw new Error('Value of ' + property + ' property should be spinner');
          }
          break;
        case 'value':
          if (parseFloat(value) < 0 || parseFloat(value) > 100) {
            throw new Error('Value of ' + property + ' property should be Number between 0-100');
          } else if (isNaN(parseFloat(value))) {
            throw new Error('Value of ' + property + ' property should be Number between 0-100');
          }
          break;
      }
    }
  }
  return defineWidget({
    name: 'mw-progress-indicator',
    widgetClass: ProgressIndicator
  });
});
